/**
 * Ask — the NL-BI console (ADR-057, A-AI-4). A question in plain words, one
 * generated SELECT over the masked `ai_bi` views, the result as a table or a
 * small chart.
 *
 * Three things this screen keeps honest:
 *
 *   - **The SQL is always shown.** An operator acting on a figure should see
 *     exactly what produced it; the generated statement sits in a collapsible
 *     block under every answer.
 *   - **A refusal is an answer.** The server refuses with a stated reason —
 *     model refusal, validator reject or the database itself — and this
 *     screen renders the reason rather than dressing it up as an error.
 *   - **Nothing here is the security boundary.** The read-only `ai_nlbi`
 *     role and the deny-token validator are; this screen could send anything
 *     and the worst outcome is a refusal on the audit record.
 */
import { useState, type ReactNode } from 'react';
import { askData, fetchAskHistory } from '../api/client.ts';
import type { AiAskCell, AiAskResult, AiRunRow } from '../api/types.ts';
import { useAsync } from '../lib/useAsync.ts';
import { age } from '../lib/datetime.ts';
import { Badge, Card, EmptyState, ErrorState } from '../ui/primitives.tsx';
import { IconAlert, IconClock, IconSearch } from '../ui/icons.tsx';

const SUGGESTIONS = [
  'Daily GGR for the last 30 days',
  'Which games made us the most money this month?',
  'How many players signed up each day this week?',
  'Top 10 players by lifetime deposits',
];

export function Ask(): ReactNode {
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [asked, setAsked] = useState<string | null>(null);
  const [result, setResult] = useState<AiAskResult | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const history = useAsync(() => fetchAskHistory(), []);

  async function submit(q: string): Promise<void> {
    const trimmed = q.trim();
    if (trimmed.length < 3 || asking) return;
    setAsking(true);
    setAsked(trimmed);
    setResult(null);
    setFailure(null);
    try {
      setResult(await askData(trimmed));
      history.reload();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Something went wrong.');
    } finally {
      setAsking(false);
    }
  }

  return (
    <div className="stack">
      <Card
        title="Ask your data"
        sub="A question in plain words becomes one read-only query over the masked reporting views — the generated SQL is always shown"
        icon={<IconSearch />}
        feature
      >
        <form
          className="toolbar"
          onSubmit={(event) => {
            event.preventDefault();
            void submit(question);
          }}
        >
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Question</span>
            <input
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="e.g. Which games made us the most money this month?"
              maxLength={500}
              disabled={asking}
            />
          </label>
          <button type="submit" className="btn btn--primary" disabled={asking || question.trim().length < 3}>
            {asking ? 'Asking…' : 'Ask'}
          </button>
        </form>
        <p className="field__hint field__hint--block">
          Try:{' '}
          {SUGGESTIONS.map((s, i) => (
            <span key={s}>
              {i > 0 ? ' · ' : ''}
              <a
                href="#/ask"
                onClick={(event) => {
                  event.preventDefault();
                  setQuestion(s);
                  void submit(s);
                }}
              >
                {s}
              </a>
            </span>
          ))}
        </p>

        {failure !== null ? <ErrorState title="The ask failed" detail={failure} /> : null}
        {result !== null ? <Answer asked={asked ?? ''} result={result} /> : null}
      </Card>

      <Card title="Recent questions" sub="Every ask is on the audit record — refusals included" icon={<IconClock />}>
        <History
          state={history}
          onRepeat={(q) => {
            setQuestion(q);
            void submit(q);
          }}
        />
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ answer

function Answer({ asked, result }: { asked: string; result: AiAskResult }): ReactNode {
  switch (result.status) {
    case 'refused':
      return (
        <div className="stack" style={{ marginTop: 'var(--space-4, 16px)' }}>
          <EmptyState title="Refused" hint={result.reason} icon={<IconAlert />} compact />
          {result.sql ? <SqlBlock sql={result.sql} /> : null}
        </div>
      );
    case 'budget_denied':
      return (
        <EmptyState
          title="Daily budget reached"
          hint="The nlbi agent hit its daily spend cap. It resets at midnight UTC; the denial is on the audit record."
          icon={<IconAlert />}
          compact
        />
      );
    case 'disabled':
      return (
        <EmptyState
          title="The ask agent is switched off"
          hint="Every AI agent ships disabled. Enabling `nlbi` in ai_config (and AI_OPS_ENABLED on the server) turns this screen on."
          icon={<IconAlert />}
          compact
        />
      );
    case 'error':
      return <ErrorState title="The ask failed" detail={`${result.message} (${result.code})`} />;
    case 'ok':
      return (
        <div className="stack" style={{ marginTop: 'var(--space-4, 16px)' }}>
          <div className="toolbar">
            <h3 style={{ margin: 0 }}>{result.title}</h3>
            <Badge tone="neutral">{result.rowCount === 500 ? '500 rows (capped)' : `${result.rowCount} rows`}</Badge>
          </div>
          {result.rowCount === 0 ? (
            <EmptyState title="No rows" hint={`“${asked}” matched nothing in the reporting views.`} compact />
          ) : (
            <>
              {result.chartHint !== 'table' ? (
                <AskChart columns={result.columns} rows={result.rows} kind={result.chartHint} />
              ) : null}
              <ResultTable columns={result.columns} rows={result.rows} />
            </>
          )}
          <SqlBlock sql={result.sql} />
        </div>
      );
  }
}

/** Operators should see what ran; collapsed so it never crowds the answer. */
function SqlBlock({ sql }: { sql: string }): ReactNode {
  return (
    <details>
      <summary className="field__hint" style={{ cursor: 'pointer' }}>
        Show the SQL that ran
      </summary>
      <pre
        className="num"
        style={{
          background: 'var(--bg-inset, rgba(0,0,0,0.2))',
          borderRadius: 8,
          padding: '10px 12px',
          overflowX: 'auto',
          whiteSpace: 'pre-wrap',
          fontSize: 12,
        }}
      >
        {sql}
      </pre>
    </details>
  );
}

function ResultTable({ columns, rows }: { columns: string[]; rows: AiAskCell[][] }): ReactNode {
  return (
    <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className={isNumeric(cell) ? 'num' : undefined}>
                  {cell === null ? '—' : String(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------------- chart

function isNumeric(v: AiAskCell): boolean {
  return v !== null && v !== '' && typeof v !== 'boolean' && Number.isFinite(Number(v));
}

/**
 * A small inline SVG, no dependencies — the Analytics screen's approach with
 * the same tokens. First column is the label axis; the first column whose
 * every value is numeric is the series. Anything the heuristic cannot chart
 * simply is not charted — the table below always carries the truth.
 */
function AskChart({
  columns,
  rows,
  kind,
}: {
  columns: string[];
  rows: AiAskCell[][];
  kind: 'line' | 'bar';
}): ReactNode {
  const seriesIdx = columns.findIndex((_, i) => i > 0 && rows.every((r) => isNumeric(r[i] ?? null)));
  if (seriesIdx === -1 || rows.length === 0) return null;
  const shown = rows.slice(0, 60);
  const labels = shown.map((r) => String(r[0] ?? ''));
  const values = shown.map((r) => Number(r[seriesIdx]));

  const W = 720;
  const H = 180;
  const PAD = { top: 12, right: 8, bottom: 22, left: 8 };
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const x = (i: number): number =>
    shown.length === 1 ? W / 2 : PAD.left + (i * (W - PAD.left - PAD.right)) / (shown.length - 1);
  const y = (v: number): number => H - PAD.bottom - ((v - min) * (H - PAD.top - PAD.bottom)) / span;
  const labelEvery = Math.max(1, Math.ceil(shown.length / 6));
  const fmtLabel = (s: string): string => (/^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(5, 10) : s.slice(0, 10));

  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={columns[seriesIdx]} style={{ display: 'block' }}>
        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={y(Math.max(min, 0))}
          y2={y(Math.max(min, 0))}
          stroke="var(--border-default)"
          strokeWidth="1"
        />
        {kind === 'line' ? (
          <path
            d={values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')}
            fill="none"
            stroke="var(--accent)"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        ) : null}
        {shown.map((_, i) => {
          const bw = Math.max(2, ((W - PAD.left - PAD.right) / shown.length) * 0.7);
          return (
            <g key={i}>
              {kind === 'bar' ? (
                <rect
                  x={x(i) - bw / 2}
                  y={Math.min(y(values[i]!), y(0))}
                  width={bw}
                  height={Math.max(1, Math.abs(y(values[i]!) - y(0)))}
                  fill="var(--accent)"
                  opacity="0.85"
                  rx="2"
                />
              ) : (
                <circle cx={x(i)} cy={y(values[i]!)} r="2.5" fill="var(--accent)" />
              )}
              <rect x={x(i) - (W / shown.length) / 2} y={0} width={W / shown.length} height={H - PAD.bottom} fill="transparent">
                <title>{`${labels[i]} — ${columns[seriesIdx]}: ${values[i]!.toLocaleString()}`}</title>
              </rect>
              {i % labelEvery === 0 || i === shown.length - 1 ? (
                <text x={x(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--text-muted)">
                  {fmtLabel(labels[i]!)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      <figcaption className="field__hint">
        {columns[seriesIdx]} by {columns[0]}
        {rows.length > shown.length ? ` · first ${shown.length} of ${rows.length} rows charted` : ''} · hover for exact figures
      </figcaption>
    </figure>
  );
}

// ----------------------------------------------------------------- history

function History({
  state,
  onRepeat,
}: {
  state: { data: AiRunRow[] | null; error: string | null; loading: boolean };
  onRepeat: (question: string) => void;
}): ReactNode {
  if (state.loading) return <p className="field__hint">Loading…</p>;
  if (state.error !== null) return <ErrorState title="Could not load the history" detail={state.error} />;
  const runs = state.data ?? [];
  if (runs.length === 0) {
    return <EmptyState title="No questions yet" hint="Everything asked here lands on the ai_runs audit record." compact />;
  }
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Question</th>
          <th>Status</th>
          <th>Rows</th>
          <th>When</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((run) => {
          const meta = (run.refMeta ?? {}) as { rowCount?: number; reason?: string };
          return (
            <tr key={run.id}>
              <td>
                <a
                  href="#/ask"
                  title={meta.reason ?? run.prompt}
                  onClick={(event) => {
                    event.preventDefault();
                    onRepeat(run.prompt);
                  }}
                >
                  {run.prompt}
                </a>
              </td>
              <td>
                <Badge tone={run.status === 'ok' ? 'win' : run.status === 'refused' ? 'neutral' : 'loss'}>
                  {run.status}
                </Badge>
              </td>
              <td className="num">{meta.rowCount ?? '—'}</td>
              <td>{age(run.createdAt)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
